import { Card, CardContent, Typography, TextField, Checkbox, FormControlLabel, Tooltip } from '@mui/material';
import { FunctionComponent, useEffect, useState } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import { LocationFormModel } from './LocationFormModel';
import { LocationGeofenceEditor } from '../LocationGeofenceEditor';
import { InfoOutlined } from '@mui/icons-material';
import { useTranslation } from '@/lib';
import { locationService } from '../../api/locations/location.service';

interface GuestLocationFormDisplayProps {
  form: UseFormReturn<LocationFormModel>;
  onSubmit: (data: LocationFormModel) => void;
  locationId?: number;
}

const GuestLocationFormDisplay: FunctionComponent<GuestLocationFormDisplayProps> = ({ form, onSubmit, locationId: _locationId }) => {
  const { control, watch } = form;
  const { t } = useTranslation();
  const [parentLocationName, setParentLocationName] = useState<string>('');
  const [isLoadingParentLocation, setIsLoadingParentLocation] = useState<boolean>(false);

  const parentLocationId = watch('parentLocationId');

  useEffect(() => {
    const fetchParentLocationName = async () => {
      if (parentLocationId) {
        setIsLoadingParentLocation(true);
        try {
          const response = await locationService.getById(parentLocationId);
          if (response.isSuccess) {
            setParentLocationName(response.payload.dto.name);
          } else {
            setParentLocationName(`Location ID: ${parentLocationId}`);
          }
        } catch (error) {
          setParentLocationName(`Location ID: ${parentLocationId}`);
        } finally {
          setIsLoadingParentLocation(false);
        }
      } else {
        setParentLocationName('');
      }
    };

    fetchParentLocationName();
  }, [parentLocationId]);

  return (
    <form onSubmit={form.handleSubmit(onSubmit)}>
      <div className="grid gap-4 lg:grid-cols-2 2xl:grid-cols-3 ">
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" className="pb-2" color="textSecondary">
              {t('details')}
            </Typography>
            <div className="flex w-full flex-col gap-y-2">
              <Controller
                name="parentLocationId"
                control={control}
                render={({ field: { value } }) => (
                  <TextField
                    label={t('parent_location')}
                    value={value ? (isLoadingParentLocation ? 'Loading...' : parentLocationName || `Location ID: ${value}`) : ''}
                    slotProps={{
                      input: {
                        readOnly: true,
                      },
                    }}
                  />
                )}
              />

              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <TextField
                    label={t('name')}
                    {...field}
                    slotProps={{
                      input: {
                        readOnly: true,
                      },
                    }}
                  />
                )}
              />

              <Controller
                name="code"
                control={control}
                render={({ field }) => (
                  <TextField
                    label={t('code')}
                    {...field}
                    slotProps={{
                      input: {
                        readOnly: true,
                      },
                    }}
                  />
                )}
              />

              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <TextField
                    label={t('description')}
                    {...field}
                    slotProps={{
                      input: {
                        readOnly: true,
                      },
                    }}
                  />
                )}
              />

              <Controller
                name="includeInReturnRate"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Checkbox {...field} checked={field.value} disabled={true} />}
                    label={
                      <span style={{ display: 'flex', alignItems: 'center' }}>
                        {t('include_in_return_rate')}
                        <Tooltip title={t('include_in_return_rate_tooltip')} placement="bottom" arrow>
                          <InfoOutlined fontSize="small" style={{ marginLeft: 4 }} />
                        </Tooltip>
                      </span>
                    }
                  />
                )}
              />
            </div>
          </CardContent>
        </Card>

        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" className="pb-2" color="textSecondary">
              {t('address')}
            </Typography>

            <div className={'flex flex-col gap-y-2'}>
              <Controller
                name="country"
                control={control}
                render={({ field: { value } }) => (
                  <TextField
                    label={t('country')}
                    value={value || ''}
                    slotProps={{
                      input: {
                        readOnly: true,
                      },
                    }}
                  />
                )}
              />
              <Controller
                name="city"
                control={control}
                render={({ field }) => (
                  <TextField
                    label={t('city')}
                    {...field}
                    slotProps={{
                      input: {
                        readOnly: true,
                      },
                    }}
                  />
                )}
              />
              <Controller
                name="streetName"
                control={control}
                render={({ field }) => (
                  <TextField
                    label={t('street_name')}
                    {...field}
                    slotProps={{
                      input: {
                        readOnly: true,
                      },
                    }}
                  />
                )}
              />
              <Controller
                name="streetNumber"
                control={control}
                render={({ field }) => (
                  <TextField
                    label={t('street_number')}
                    {...field}
                    slotProps={{
                      input: {
                        readOnly: true,
                      },
                    }}
                  />
                )}
              />
              <Controller
                name="postalCode"
                control={control}
                render={({ field }) => (
                  <TextField
                    label={t('postal_code')}
                    {...field}
                    slotProps={{
                      input: {
                        readOnly: true,
                      },
                    }}
                  />
                )}
              />
            </div>
          </CardContent>
        </Card>

        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" className="pb-2" color="textSecondary">
              {t('geofence')}
            </Typography>

            <div className={'flex flex-col'}>
              <Controller
                name="geofence"
                control={control}
                render={({ field: { value } }) => (
                  <LocationGeofenceEditor
                    onPolygonDrawn={() => {}} // No-op for guest mode
                    polygon={value ?? []}
                  />
                )}
              />
            </div>
          </CardContent>
        </Card>
      </div>
      {/* No action bar for guest users */}
    </form>
  );
};

export default GuestLocationFormDisplay;
